import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import { Box, Chip } from "@mui/material";

import {
  PermissionLevel,
  BulletinShareByBulletinItem,
  GroupShareInfo,
} from "./types";
export const getPermissionLevel = (
  canView: boolean,
  canModify: boolean,
): PermissionLevel => {
  if (!canView && !canModify) return "none";
  if (canView && !canModify) return "read";
  if (canView && canModify) return "full";
  return "none";
};

export const getActiveStep = (
  level: PermissionLevel,
  maxPermission: number = 2,
): number => {
  // Create step map based on maxPermission
  // maxPermission: 0=none only, 1=none+read, 2=none+read+full
  const getStepMap = () => {
    if (maxPermission === 0) {
      return { none: 0, read: 0, full: 0 }; // Only "none" step exists
    } else if (maxPermission === 1) {
      return { none: 0, read: 1, full: 1 }; // Map 'full' to 'read' step index
    }
    return { none: 0, read: 1, full: 2 }; // Default: all steps available
  };

  const stepMap = getStepMap();
  return stepMap[level];
};

export const getAgencyBadge = (agency?: string) => {
  if (!agency) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <LocalPoliceIcon fontSize="small" />
      {agency}
    </Box>
  );
};

export const formatExpiration = (
  time_expires?: Date | null,
  isShared: boolean = false,
) => {
  // If item is shared and has no expiration date, show "no expire"
  if (isShared && !time_expires) {
    return (
      <Chip label="Never" size="small" color="success" variant="outlined" />
    );
  }

  // If not shared or no expiration date, show "-"
  if (!time_expires) return "-";

  const expireDate = new Date(time_expires);
  const now = new Date();
  const isExpired = expireDate <= now;

  return (
    <Chip
      label={expireDate.toLocaleDateString()}
      size="small"
      color={isExpired ? "error" : "default"}
      variant={isExpired ? "filled" : "outlined"}
    />
  );
};

// Helper function to check if a group has existing shares
export const getGroupShareInfo = (
  groupId: number,
  bulletinShares: BulletinShareByBulletinItem[],
  sharesFromCurrentUser: Set<number>,
): GroupShareInfo => {
  const groupShares = bulletinShares.filter(
    (share) => share.bulletin_share.destination_group_id === groupId,
  );

  const hasShares = groupShares.length > 0;
  const hasSharesFromCurrentUser = groupShares.some((share) =>
    sharesFromCurrentUser.has(share.bulletin_share.id),
  );

  const shareFromCurrentUser = groupShares.find((share) =>
    sharesFromCurrentUser.has(share.bulletin_share.id),
  );

  return {
    hasShares,
    hasSharesFromCurrentUser,
    shareFromCurrentUser: shareFromCurrentUser || null,
    allShares: groupShares,
  };
};
